import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import SvgIcon from "../../../../../parts/SvgIcon";
import { customRound } from "../../../../../utils/nutrition-util";

const NS = "recipeDetail";

const FOOD_TYPE_I18N = {
  breakfast: "breakfast",
  "lunch/dinner": "lunchDinner",
  snack: "snack",
};

const MACRO_KEYS = ["proteins", "carbs", "fats", "fibers"];

const labelize = (value) =>
  String(value)
    .split(/([\s-]+)/)
    .map((part) =>
      /^[\s-]+$/.test(part)
        ? part
        : part.charAt(0).toUpperCase() + part.slice(1)
    )
    .join("");

const hasNutrition = (recipe) =>
  recipe.kcal > 0 || MACRO_KEYS.some((key) => recipe.nutrients[key] > 0);

const getFoodTypeLabel = (t, foodType) => {
  if (!foodType) return null;
  const key = FOOD_TYPE_I18N[foodType];
  return key ? t(`${NS}.foodTypes.${key}`) : labelize(foodType);
};

const getEnumLabel = (t, group, value) =>
  value
    ? t(`${NS}.${group}.${value}`, { defaultValue: labelize(value) })
    : null;

const HeroStat = ({ icon, children }) => (
  <span className="recipe-detail-stat">
    {icon && (
      <SvgIcon
        src={icon}
        useCurrentColor
        classNames="recipe-detail-stat-icon"
      />
    )}
    <span>{children}</span>
  </span>
);

HeroStat.propTypes = {
  icon: PropTypes.string,
  children: PropTypes.node.isRequired,
};

const HeroStats = ({ recipe }) => {
  const { t } = useTranslation("athlete-portal");
  const difficultyLabel = getEnumLabel(
    t,
    "difficultyLevels",
    recipe.difficulty
  );
  const foodTypeLabel = getFoodTypeLabel(t, recipe.foodType);

  return (
    <div className="recipe-detail-stats">
      {recipe.prepTime ? (
        <HeroStat icon="/img/new-icons/vuesax-linear-clock.svg">
          {recipe.prepTime}
        </HeroStat>
      ) : null}
      {recipe.servings ? (
        <HeroStat icon="/img/new-icons/people-circle-outline.svg">
          {t(`${NS}.servings`, { count: recipe.servings })}
        </HeroStat>
      ) : null}
      {difficultyLabel ? (
        <HeroStat>
          {t(`${NS}.difficulty`)}: {difficultyLabel}
        </HeroStat>
      ) : null}
      {foodTypeLabel ? <HeroStat>{foodTypeLabel}</HeroStat> : null}
    </div>
  );
};

HeroStats.propTypes = { recipe: PropTypes.object.isRequired };

const NutritionSection = ({ recipe }) => {
  const { t } = useTranslation("athlete-portal");
  const macrosBaseLabel = getEnumLabel(t, "macrosBaseTypes", recipe.macrosBase);

  return (
    <section className="recipe-detail-section">
      <div className="recipe-detail-nutrition-header">
        <h3>{t(`${NS}.nutrition`)}</h3>
        {macrosBaseLabel ? (
          <span className="recipe-detail-nutrition-base">
            {macrosBaseLabel}
          </span>
        ) : null}
      </div>
      <div className="recipe-detail-nutrition">
        <div className="recipe-detail-kcal">
          <strong>{recipe.kcal}</strong>
          <span>{t(`${NS}.kcal`)}</span>
        </div>
        <div className="recipe-detail-macros">
          {MACRO_KEYS.map((key) => (
            <div key={key} className="recipe-detail-macro">
              <strong>{customRound(recipe.nutrients[key])}g</strong>
              <span>{t(`${NS}.macros.${key}`)}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

NutritionSection.propTypes = { recipe: PropTypes.object.isRequired };

const IngredientsSection = ({ ingredients }) => {
  const { t } = useTranslation("athlete-portal");

  return (
    <section className="recipe-detail-section">
      <h3>{t(`${NS}.ingredients`)}</h3>
      <ul className="recipe-detail-ingredients">
        {ingredients.map((item, index) => (
          <li key={`${item.name}-${index}`}>
            <span className="recipe-detail-ingredient-name">
              {labelize(item.name)}
            </span>
            {item.quantity ? (
              <span className="recipe-detail-ingredient-qty">
                {item.quantity}
              </span>
            ) : null}
          </li>
        ))}
      </ul>
    </section>
  );
};

IngredientsSection.propTypes = {
  ingredients: PropTypes.arrayOf(PropTypes.object).isRequired,
};

const StepsSection = ({ steps }) => {
  const { t } = useTranslation("athlete-portal");

  return (
    <section className="recipe-detail-section">
      <h3>{t(`${NS}.instructions`)}</h3>
      <ol className="recipe-detail-steps">
        {steps.map((step, index) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: recipe steps are ordered strings without unique IDs
          <li key={`${step}-${index}`}>{step}</li>
        ))}
      </ol>
    </section>
  );
};

StepsSection.propTypes = {
  steps: PropTypes.arrayOf(PropTypes.string).isRequired,
};

const BadgesSection = ({ title, items }) => (
  <section className="recipe-detail-section">
    <h3>{title}</h3>
    <div className="recipe-detail-badges">
      {items.map((label) => (
        <span key={label}>{label}</span>
      ))}
    </div>
  </section>
);

BadgesSection.propTypes = {
  title: PropTypes.string.isRequired,
  items: PropTypes.arrayOf(PropTypes.string).isRequired,
};

const RecipeBody = ({ recipe }) => {
  const { t } = useTranslation("athlete-portal");
  const dietaryLabels = recipe.dietaryRestrictions.map(labelize);

  return (
    <>
      {hasNutrition(recipe) && <NutritionSection recipe={recipe} />}
      {recipe.description && (
        <section className="recipe-detail-section">
          <h3>{t(`${NS}.about`)}</h3>
          <p>{recipe.description}</p>
        </section>
      )}
      {recipe.ingredients.length > 0 && (
        <IngredientsSection ingredients={recipe.ingredients} />
      )}
      {recipe.steps.length > 0 && <StepsSection steps={recipe.steps} />}
      {dietaryLabels.length > 0 && (
        <BadgesSection title={t(`${NS}.dietary`)} items={dietaryLabels} />
      )}
    </>
  );
};

RecipeBody.propTypes = { recipe: PropTypes.object.isRequired };

const SkeletonBlock = ({ className }) => (
  <div className={`animate-pulse rounded-lg bg-(--white-10) ${className}`} />
);

SkeletonBlock.propTypes = { className: PropTypes.string.isRequired };

const SKELETON_MACROS = ["proteins", "carbs", "fats", "fibers"];
const SKELETON_INGREDIENTS = ["first", "second", "third", "fourth"];
const SKELETON_STEPS = ["first", "second", "third"];

const RecipeDetailSkeleton = ({ label }) => (
  <output className="flex flex-col gap-5" aria-label={label}>
    <section>
      <SkeletonBlock className="mb-3 h-4 w-24" />
      <div className="flex flex-wrap items-stretch gap-3">
        <SkeletonBlock className="h-20 w-26" />
        <div className="grid min-w-0 flex-1 grid-cols-2 gap-2 sm:grid-cols-4">
          {SKELETON_MACROS.map((key) => (
            <SkeletonBlock key={key} className="h-20" />
          ))}
        </div>
      </div>
    </section>
    <section>
      <SkeletonBlock className="mb-3 h-4 w-28" />
      <div className="flex flex-col gap-2">
        {SKELETON_INGREDIENTS.map((key) => (
          <SkeletonBlock key={key} className="h-9" />
        ))}
      </div>
    </section>
    <section>
      <SkeletonBlock className="mb-3 h-4 w-28" />
      <div className="flex flex-col gap-3">
        {SKELETON_STEPS.map((key) => (
          <SkeletonBlock key={key} className="h-5" />
        ))}
      </div>
    </section>
  </output>
);

RecipeDetailSkeleton.propTypes = { label: PropTypes.string.isRequired };

const ERROR_MESSAGE_KEYS = {
  notFound: `${NS}.errors.notFound`,
  error: `${NS}.errors.generic`,
};

export const RecipeDetailView = ({ recipe, status, closeHref }) => {
  const { t } = useTranslation("athlete-portal");

  const isLoading = status === "loading";
  const showRecipe = status === "ready" && recipe;
  const errorMessageKey = ERROR_MESSAGE_KEYS[status];

  return (
    <div className="recipe-detail">
      <a
        href={closeHref}
        className="recipe-detail-close"
        aria-label={t(`${NS}.close`)}
      >
        <img src="/img/new-icons/red-cross.svg" alt="" />
      </a>

      <div className="recipe-detail-hero">
        {recipe?.image ? (
          <img src={recipe.image} alt="" />
        ) : (
          <div className="recipe-detail-hero-empty" />
        )}
        <div className="recipe-detail-hero-fade" />
        <div className="recipe-detail-hero-content">
          {isLoading ? (
            <>
              <SkeletonBlock className="h-7 w-2/3 bg-(--white-20)!" />
              <SkeletonBlock className="h-4 w-1/2 bg-(--white-20)!" />
            </>
          ) : (
            <h2>{recipe?.title || "—"}</h2>
          )}
          {showRecipe && <HeroStats recipe={recipe} />}
        </div>
      </div>

      <div className="recipe-detail-body">
        {isLoading && <RecipeDetailSkeleton label={t(`${NS}.loading`)} />}
        {errorMessageKey && (
          <div className="recipe-detail-error">
            <p>{t(`${NS}.errors.title`)}</p>
            <span>{t(errorMessageKey)}</span>
          </div>
        )}
        {showRecipe && <RecipeBody recipe={recipe} />}
      </div>
    </div>
  );
};

RecipeDetailView.propTypes = {
  recipe: PropTypes.object,
  status: PropTypes.oneOf(["loading", "ready", "notFound", "error"]).isRequired,
  closeHref: PropTypes.string.isRequired,
};
